import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState, type JSX } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import numeral from 'numeral';
import type { EChartsOption } from 'echarts';

import useFormatAmount from 'app-components/hooks/privacyMode/useFormatAmount';
import usePrivacyMaskValue from 'app-components/hooks/privacyMode/usePrivacyMaskValue';
import { buildSidewaysBarChartOption } from 'utils/chart-options';
import type { SidewaysBarChartConfig, SidewaysBarDatum } from 'utils/chart-options';
import { buildPieDisplayData, MAX_PIE_DISPLAY_ITEMS } from 'utils/chart-options/pie/displayData';
import BreadcrumbNav from 'containers/Transactions/components/TransactionGraphRegister/components/BreadcrumbNav';
import type { Breadcrumb } from 'containers/Transactions/components/TransactionGraphRegister/types';

import EChartsBase from '../EChartsBase';
import useEChartsTheme from '../useEChartsTheme';

const DEFAULT_BREADCRUMBS: Breadcrumb[] = [];
const DEFAULT_ORDERED_FIELDS: string[] = [];
const MAX_LEGEND_ITEMS = MAX_PIE_DISPLAY_ITEMS;
const CHART_CANVAS_HEIGHT = 800;

/**
 * `full` — OpenSaveReports drill-down chart: ordered/breadcrumb-consistent colors, "Everything
 *          else" aggregation with a "Show all" toggle, and the detailed dot + amount tooltip.
 * `simple` — TransactionGraphRegister chart: value-sorted, index-based colors, and a plain
 *            "Category: amount" tooltip. No aggregation / toggle.
 */
export type SidewaysBarChartVariant = 'full' | 'simple';

export interface SidewaysBarChartData {
  category: string;
  total: number;
  displayTotal?: number;
  categoryId?: string;
  isEverythingElse?: boolean;
  remainingItems?: SidewaysBarChartData[];
  showSign?: 'positive' | 'negative';
  [key: string]: unknown;
}

export interface SidewaysBarChartProps {
  data: SidewaysBarChartData[];
  variant?: SidewaysBarChartVariant;
  height?: number | string;
  /** Drives `full` coloring; ignored by `simple` (which colors by index). */
  orderedFields?: string[];
  onBarClick?: (data: SidewaysBarChartData) => void;
  breadcrumbs?: Breadcrumb[];
  onBreadcrumbClick?: (breadcrumbs: Breadcrumb[]) => void;
  onChartTransitionFinished?: () => void;
  /** Label noun for the `full` variant's "Show all/top" button. */
  rowType?: string;
  animated?: boolean;
  /** ECharts renderer. Canvas by default — the SVG renderer skips the bar entry animation. */
  renderer?: 'svg' | 'canvas';
  optionOverrides?: Record<string, unknown> | ((option: EChartsOption) => EChartsOption);
}

export type SidewaysBarChartRef = {
  // Optional: this chart has no persistent selection to clear, so callers must guard the call.
  clearSelection?: () => void;
};

const isRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object';

const getOriginalColorIndex = (crumb: Breadcrumb | undefined): number => {
  if (!isRecord(crumb) || !('originalColorIndex' in crumb)) return 0;
  const value = crumb.originalColorIndex;
  return typeof value === 'number' ? value : 0;
};

const getOriginalRowId = (item: SidewaysBarChartData): string | undefined => {
  if (!isRecord(item.originalRow) || typeof item.originalRow.id !== 'string') return undefined;
  return item.originalRow.id;
};

const SidewaysBarChart = forwardRef<SidewaysBarChartRef, SidewaysBarChartProps>(function SidewaysBarChart(
  {
    data,
    variant = 'full',
    height,
    orderedFields = DEFAULT_ORDERED_FIELDS,
    onBarClick,
    breadcrumbs = DEFAULT_BREADCRUMBS,
    onBreadcrumbClick,
    onChartTransitionFinished,
    rowType,
    animated = true,
    renderer = 'canvas',
    optionOverrides,
  },
  ref,
): JSX.Element {
  const theme = useEChartsTheme();
  const formatAmount = useFormatAmount();
  const privacyMask = usePrivacyMaskValue();
  const [showAllItems, setShowAllItems] = useState(false);
  const onChartTransitionFinishedRef = useRef(onChartTransitionFinished);
  onChartTransitionFinishedRef.current = onChartTransitionFinished;

  const isSimple = variant === 'simple';

  // No persistent selection to clear — expose an optional no-op so the ref shape stays uniform.
  useImperativeHandle(ref, () => ({ clearSelection: () => {} }));

  const formatRowType = useMemo(() => {
    if (!rowType) return 'items';
    const singularToPlural: Record<string, string> = {
      category: 'categories',
      payee: 'payees',
      tag: 'tags',
      account: 'accounts',
      business: 'businesses',
      usage: 'usages',
    };
    const formatted = rowType.toLowerCase().replace(/_/g, ' ');
    return singularToPlural[formatted] || `${formatted}s`;
  }, [rowType]);

  // The exact same visual projection as LegendPieChart: sorted report rows plus the optional
  // folded tail bucket. `simple` is always expanded because it has no tail control.
  const processedData = useMemo<SidewaysBarChartData[]>(() => {
    return buildPieDisplayData(data ?? [], isSimple || showAllItems, MAX_LEGEND_ITEMS) as SidewaysBarChartData[];
  }, [data, isSimple, showAllItems]);

  const { minValue, maxValue } = useMemo(() => {
    let min = 0;
    let max = 0;
    processedData.forEach((datum) => {
      if (datum?.total < min) min = datum.total;
      if (datum?.total > max) max = datum.total;
    });
    return { minValue: min, maxValue: max };
  }, [processedData]);

  // Match LegendPieChart's palette-index contract. Colors are tied to the value-sorted report
  // data, not an incoming array position, so pie and sideways views retain the same category
  // colors and drill-downs retain the selected parent color.
  const sortedColorData = useMemo(() => buildPieDisplayData(data ?? [], true, MAX_LEGEND_ITEMS), [data]);

  const getColorIndex = useCallback(
    (item: SidewaysBarChartData, visualIndex: number): number => {
      if (item.isEverythingElse) return MAX_LEGEND_ITEMS;

      if (breadcrumbs.length > 1) {
        const baseColorIndex = getOriginalColorIndex(breadcrumbs[breadcrumbs.length - 1]);
        return visualIndex === 0 ? baseColorIndex : baseColorIndex + visualIndex;
      }

      const originalRowId = getOriginalRowId(item);
      if (originalRowId) {
        const indexById = sortedColorData.findIndex((dataItem) => getOriginalRowId(dataItem) === originalRowId);
        if (indexById >= 0) return indexById;
      }

      if (item.category) {
        const indexByCategory = sortedColorData.findIndex((dataItem) => dataItem.category === item.category);
        if (indexByCategory >= 0) return indexByCategory;
      }

      if (item.categoryId) {
        const indexByCategoryId = sortedColorData.findIndex(
          (dataItem) => dataItem.categoryId === item.categoryId && dataItem.category === item.category,
        );
        if (indexByCategoryId >= 0) return indexByCategoryId;
      }

      const fieldIndex = orderedFields.indexOf(item.category);
      return fieldIndex >= 0 ? fieldIndex : visualIndex;
    },
    [breadcrumbs, orderedFields, sortedColorData],
  );

  // Both variants use the pie chart's category-to-palette-index matching. Transaction-page
  // drill-downs use the `simple` variant, but still need to retain the selected parent color.
  const resolveColor = useCallback(
    (item: SidewaysBarChartData, index: number): string => {
      const { colors } = theme;
      return colors[getColorIndex(item, index) % colors.length] ?? colors[0];
    },
    [theme, getColorIndex],
  );

  const bars = useMemo<SidewaysBarDatum[]>(
    () =>
      processedData.map((item, index) => {
        let formattedAmount: string;
        if (isSimple) {
          formattedAmount = numeral(item.total ?? 0).format('$0,0.00');
        } else {
          const displayAmount = item.displayTotal ?? item.total ?? 0;
          const sign = item.showSign === 'positive' ? '+' : item.showSign === 'negative' ? '-' : '';
          formattedAmount = `${sign}${numeral(item.showSign ? Math.abs(displayAmount) : displayAmount).format(
            '$0,0.00',
          )}`;
        }
        return {
          id: getOriginalRowId(item) ?? item.categoryId ?? `${item.category}-${index}`,
          category: item.category,
          value: item.total,
          color: resolveColor(item, index),
          label: formattedAmount,
          tooltipAmount: formattedAmount,
        };
      }),
    [processedData, isSimple, resolveColor],
  );

  const option = useMemo<EChartsOption>(() => {
    const config: SidewaysBarChartConfig = {
      theme,
      animated,
      formatAmount,
      privacyMask,
      minValue,
      maxValue,
      clickable: !!onBarClick,
      simpleTooltip: isSimple,
    };
    return buildSidewaysBarChartOption(bars, config);
  }, [bars, theme, animated, formatAmount, privacyMask, minValue, maxValue, onBarClick, isSimple]);

  const onEvents = useMemo(
    () => ({
      finished: () => onChartTransitionFinishedRef.current?.(),
      click: (params: { componentType?: string; dataIndex?: number }) => {
        if (params.componentType !== 'series' || typeof params.dataIndex !== 'number') return;
        const item = processedData[params.dataIndex];
        if (!item) return;

        if (isSimple) {
          // The transactions-page drill-down state must receive this identity so its next
          // breadcrumb can retain the parent palette position.
          onBarClick?.({ ...item, originalColorIndex: getColorIndex(item, params.dataIndex) });
          return;
        }

        if (item.isEverythingElse) {
          setShowAllItems(true);
          return;
        }

        const originalColorIndex = getColorIndex(item, params.dataIndex);
        onBarClick?.({ ...item, originalColorIndex });
      },
    }),
    [processedData, isSimple, getColorIndex, onBarClick],
  );

  let finalOption = option;
  if (typeof optionOverrides === 'function') {
    finalOption = optionOverrides(option);
  } else if (optionOverrides) {
    finalOption = { ...option, ...optionOverrides };
  }

  const chartViewportHeight = height ?? processedData.length * 28 + 30;
  const chartCanvasHeight =
    typeof chartViewportHeight === 'number' ? Math.max(CHART_CANVAS_HEIGHT, chartViewportHeight) : chartViewportHeight;
  const chart = (
    <EChartsBase
      option={finalOption}
      onEvents={onEvents}
      height={chartCanvasHeight}
      renderer={renderer}
      style={{ left: 0, position: 'absolute', top: 0 }}
      testId='echart-sideways-bar-chart'
    />
  );

  // `full` shows breadcrumbs only once drilled in (>1); `simple` shows them at any depth (>0).
  const breadcrumbThreshold = isSimple ? 0 : 1;
  const showToggle = !isSimple && data && data.length > MAX_LEGEND_ITEMS && breadcrumbs.length <= 1;

  return (
    <Box>
      {breadcrumbs && breadcrumbs.length > breadcrumbThreshold && onBreadcrumbClick && (
        <Box mb={2}>
          <BreadcrumbNav breadcrumbs={breadcrumbs} onBreadcrumbClick={onBreadcrumbClick} />
        </Box>
      )}

      <Box
        sx={{
          height: chartViewportHeight,
          overflow: 'hidden',
          position: 'relative',
          transition: 'height 300ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        {chart}
      </Box>

      {showToggle && (
        <Box display='flex' justifyContent='center' mt={2}>
          <Button
            variant='tonal'
            size='small'
            onClick={() => setShowAllItems(!showAllItems)}
            sx={{ fontSize: '0.75rem', textTransform: 'none' }}
            endIcon={showAllItems ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          >
            {showAllItems ? `Show top ${formatRowType}` : `Show all ${formatRowType}`}
          </Button>
        </Box>
      )}
    </Box>
  );
});

export default SidewaysBarChart;
